import { useEffect, useState } from "react";
import { api, photoUrl, errText } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { useLanguage } from "../../context/LanguageContext";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Avatar, AvatarFallback, AvatarImage } from "../ui/avatar";
import { Switch } from "../ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { toast } from "sonner";
import { Loader2, ShieldCheck, Mail, KeyRound, Ban } from "lucide-react";

export function TwoFactorCard({ user }) {
  const { checkAuth } = useAuth();
  const { t } = useLanguage();
  const [on, setOn] = useState(!!user?.two_factor_enabled);
  const [saving, setSaving] = useState(false);
  const toggle = async (v) => {
    setOn(v); setSaving(true);
    try {
      await api.put("/auth/me/two-factor", { enabled: v });
      toast.success(v ? "Two-factor login enabled" : "Two-factor login disabled");
      checkAuth();
    } catch (e) { setOn(!v); toast.error(errText(e)); }
    finally { setSaving(false); }
  };
  return (
    <div className="max-w-xl bg-white rounded-2xl border border-slate-200/80 p-6" data-testid="twofactor-card">
      <div className="flex items-start gap-4">
        <span className="h-10 w-10 rounded-xl bg-[#FFF4F0] flex items-center justify-center shrink-0"><ShieldCheck size={20} className="text-[#F5551E]" /></span>
        <div className="flex-1">
          <p className="font-display font-bold text-[#12234A]">{t("d_2fa_title")}</p>
          <p className="text-sm text-slate-500 mt-0.5">{t("d_2fa_desc")}</p>
        </div>
        <Switch checked={on} disabled={saving} onCheckedChange={toggle} data-testid="twofactor-toggle" />
      </div>
    </div>
  );
}

export function SecurityQuestionCard() {
  const { t } = useLanguage();
  const [options, setOptions] = useState([]);
  const [current, setCurrent] = useState(null);
  const [question, setQuestion] = useState("");
  const [answer, setAnswer] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    api.get("/auth/me/security-question").then((r) => {
      setOptions(r.data.options || []);
      setCurrent(r.data.question || null);
      setQuestion(r.data.question || "");
    }).catch(() => {});
  }, []);
  const save = async () => {
    if (!question) return toast.error("Choose a security question");
    if (answer.trim().length < 2) return toast.error("Enter an answer");
    setSaving(true);
    try {
      await api.put("/auth/me/security-question", { question, answer: answer.trim() });
      setCurrent(question); setAnswer("");
      toast.success("Security question saved");
    } catch (e) { toast.error(errText(e)); }
    finally { setSaving(false); }
  };
  return (
    <div className="max-w-xl bg-white rounded-2xl border border-slate-200/80 p-6" data-testid="security-question-card">
      <div className="flex items-start gap-4">
        <span className="h-10 w-10 rounded-xl bg-[#EEF4FF] flex items-center justify-center shrink-0"><KeyRound size={20} className="text-[#12234A]" /></span>
        <div className="flex-1">
          <p className="font-display font-bold text-[#12234A]">{t("d_secq_title")}</p>
          <p className="text-sm text-slate-500 mt-0.5">
            {current ? t("d_secq_set") : t("d_secq_unset")}
          </p>
        </div>
      </div>
      <div className="mt-4 space-y-3">
        <Select value={question} onValueChange={setQuestion}>
          <SelectTrigger data-testid="security-question-select" className="h-11 rounded-xl"><SelectValue placeholder={t("d_secq_choose")} /></SelectTrigger>
          <SelectContent className="bg-white">
            {options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
          </SelectContent>
        </Select>
        <Input value={answer} onChange={(e) => setAnswer(e.target.value)} data-testid="security-question-answer" className="h-11 rounded-xl" placeholder={current ? "Enter a new answer to update" : "Your answer"} />
        <Button onClick={save} disabled={saving} data-testid="security-question-save" className="rounded-xl bg-[#12234A] hover:bg-[#0d1a38] text-white gap-1.5">
          {saving ? <Loader2 size={15} className="animate-spin" /> : <KeyRound size={15} />} {current ? t("d_secq_update") : t("d_secq_save")}
        </Button>
      </div>
    </div>
  );
}

export function BlockedUsersCard() {
  const { t } = useLanguage();
  const [list, setList] = useState(null);
  const load = () => api.get("/me/blocked").then((r) => setList(r.data.blocked)).catch(() => setList([]));
  useEffect(() => { load(); }, []);
  const unblock = async (id) => {
    try { await api.delete(`/users/${id}/block`); toast.success("Unblocked"); load(); }
    catch (e) { toast.error(errText(e)); }
  };
  if (!list) return null;
  return (
    <div className="max-w-xl bg-white rounded-2xl border border-slate-200/80 p-6" data-testid="blocked-users-card">
      <div className="flex items-start gap-4">
        <span className="h-10 w-10 rounded-xl bg-slate-100 flex items-center justify-center shrink-0"><Ban size={20} className="text-slate-500" /></span>
        <div className="flex-1">
          <p className="font-display font-bold text-[#12234A]">{t("d_blocked_title")}</p>
          <p className="text-sm text-slate-500 mt-0.5">{list.length ? t("d_blocked_some") : t("d_blocked_none")}</p>
        </div>
      </div>
      {list.length > 0 && (
        <div className="mt-4 space-y-2">
          {list.map((u) => (
            <div key={u.user_id} className="flex items-center gap-3 p-2 rounded-xl bg-slate-50" data-testid={`blocked-${u.user_id}`}>
              <Avatar className="h-8 w-8"><AvatarImage src={photoUrl(u.picture)} /><AvatarFallback className="bg-[#12234A] text-white text-xs">{u.name?.[0]}</AvatarFallback></Avatar>
              <p className="flex-1 text-sm font-medium text-[#12234A] truncate">{u.name}</p>
              <Button onClick={() => unblock(u.user_id)} data-testid={`unblock-${u.user_id}`} variant="outline" size="sm" className="rounded-xl">{t("d_unblock")}</Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function NotificationPrefs({ user }) {
  const { t } = useLanguage();
  const [on, setOn] = useState(user?.weekly_email !== false);
  const [saving, setSaving] = useState(false);
  const toggle = async (v) => {
    setOn(v); setSaving(true);
    try { await api.put("/auth/me/preferences", { weekly_email: v }); toast.success(v ? "Weekly recap on" : "Weekly recap off"); }
    catch (e) { setOn(!v); toast.error(errText(e)); }
    finally { setSaving(false); }
  };
  return (
    <div className="max-w-xl bg-white rounded-2xl border border-slate-200/80 p-6" data-testid="notification-prefs">
      <div className="flex items-start gap-4">
        <span className="h-10 w-10 rounded-xl bg-[#EEF4FF] flex items-center justify-center shrink-0"><Mail size={20} className="text-[#12234A]" /></span>
        <div className="flex-1">
          <p className="font-display font-bold text-[#12234A]">{t("d_weekly_title")}</p>
          <p className="text-sm text-slate-500 mt-0.5">{t("d_weekly_desc")}</p>
        </div>
        <Switch checked={on} disabled={saving} onCheckedChange={toggle} data-testid="weekly-email-toggle" />
      </div>
    </div>
  );
}
